import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

import PagarForma from "./PagarForma";
import PagarQuem from "./PagarQuem";
import { PagueNoCaixa, PixGerado } from "./PagarPix";
import { contaDaMesa, pagarMesa } from "./api";

// Pagar pelo celular (pedido do dono, 24/09/2026): de quem → forma → PIX ou
// "pague no caixa". Pago, abre o comprovante da pessoa (`onPago`), que é o
// link com o QR de saída. Cada um paga a sua parte sem travar a mesa dos
// outros; os produtos pagos ficam marcados na comanda de todo mundo.
export default function PagamentoMesa({ token, quem, mesa, eu, fotoDe, onFechar, onPago, darkMode }) {
  const [conta, setConta] = useState(null);
  const [erro, setErro] = useState(null);
  const [etapa, setEtapa] = useState("quem");
  const [escolha, setEscolha] = useState({ escopo: "meus", pessoas: [] });
  const [forma, setForma] = useState("pix");
  const [usarCashback, setUsarCashback] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [pix, setPix] = useState(null);

  useEffect(() => {
    let vivo = true;
    contaDaMesa(token, quem).then((r) => {
      if (!vivo) return;
      if (!r.ok) return setErro(r.erro?.mensagem || "Não consegui abrir a conta.");
      setConta(r.dados);
      // Caixa recebeu "por valor" (sem produto): só a mesa toda desconta isso
      if (!(r.dados.meu_restante > 0) || r.dados.pago_por_valor > 0) setEscolha({ escopo: "mesa", pessoas: [] });
      if (quem?.cliente_id) setEscolha((e) => (e.escopo === "pessoas" ? e : { ...e, pessoas: [quem.cliente_id] }));
    });
    return () => { vivo = false; };
  }, [token, quem]);

  const valor = useMemo(() => {
    if (!conta) return 0;
    if (escolha.escopo === "meus") return conta.meu_restante;
    if (escolha.escopo === "mesa") return conta.mesa_restante;
    return (conta.pessoas || []).filter((p) => escolha.pessoas.includes(p.cliente_id))
      .reduce((s, p) => s + Number(p.restante || 0), 0);
  }, [conta, escolha]);

  // Base do cashback: o servidor não usa carteira em produto em promoção
  const elegivel = useMemo(() => {
    if (!conta) return 0;
    const pessoas = conta.pessoas || [];
    if (escolha.escopo === "mesa") return Number(conta.mesa_elegivel ?? conta.mesa_restante ?? 0);
    const alvo = escolha.escopo === "meus" ? pessoas.filter((p) => p.eu) : pessoas.filter((p) => escolha.pessoas.includes(p.cliente_id));
    return alvo.reduce((s, p) => s + Number(p.elegivel ?? p.restante ?? 0), 0);
  }, [conta, escolha]);

  const escolher = (nova) => setEscolha(nova.escopo === "pessoas" && !nova.pessoas.length
    ? { escopo: "pessoas", pessoas: [quem?.cliente_id].filter(Boolean) } : nova);

  const gerar = async () => {
    setGerando(true);
    const r = await pagarMesa(token, quem, { escopo: escolha.escopo, pessoas: escolha.pessoas, usar_cashback: usarCashback });
    setGerando(false);
    if (!r.ok) return toast.error(r.erro?.mensagem || "Não consegui gerar o pagamento.");
    if (r.dados?.pagamento?.status === "aprovado") return onPago();
    setPix(r.dados);
    setEtapa("pix");
  };

  const avancar = () => {
    if (etapa === "quem") return setEtapa("forma");
    if (forma !== "pix" || !conta?.pix_online) return setEtapa("caixa");
    return gerar();
  };

  const voltar = () => (etapa === "quem" ? onFechar() : setEtapa(etapa === "forma" ? "quem" : "forma"));
  const fundo = darkMode ? "bg-zinc-950 text-zinc-100" : "bg-white text-zinc-900";
  const titulos = { quem: "Pagar a conta", forma: "Como você vai pagar?", caixa: "Pagar no caixa", pix: "PIX" };
  const podeAvancar = valor > 0 && !(escolha.escopo === "pessoas" && !escolha.pessoas.length);

  return (
    <div className={`fixed inset-0 z-[70] flex flex-col ${fundo}`} data-e2e="mesa-pagamento">
      <div className="px-4 pt-5 pb-3 flex items-center gap-2">
        <button type="button" onClick={voltar} className="p-2 -ml-2" aria-label="Voltar"><ArrowLeft className="w-5 h-5" /></button>
        <h2 className="text-lg font-black">{titulos[etapa]}</h2>
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {erro ? <p className="text-sm text-red-500 py-10 text-center">{erro}</p> : null}
        {!conta && !erro ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin opacity-60" /></div> : null}
        {conta && conta.divisao_ativa && etapa !== "pix" ? (
          <p className="rounded-2xl bg-amber-500/10 text-amber-600 text-sm px-4 py-3 mb-3">
            A conta desta mesa está sendo dividida no caixa agora. Chame o atendente para pagar.
          </p>
        ) : null}
        {conta && etapa === "quem" ? (
          <PagarQuem conta={conta} escolha={escolha} onEscolher={escolher} fotoDe={fotoDe} darkMode={darkMode} />
        ) : null}
        {conta && etapa === "forma" ? (
          <PagarForma forma={forma} onForma={setForma} valor={valor} elegivel={elegivel} cashback={conta.cashback}
            usarCashback={usarCashback} onUsarCashback={setUsarCashback} pixOnline={conta.pix_online} darkMode={darkMode} />
        ) : null}
        {etapa === "caixa" ? <PagueNoCaixa mesa={mesa} eu={eu} forma={forma} darkMode={darkMode} /> : null}
        {etapa === "pix" && pix ? (
          <PixGerado token={token} quem={quem} pagamento={pix.pagamento} cobranca={pix.cobranca} onPago={onPago} darkMode={darkMode} />
        ) : null}
      </div>
      {conta && (etapa === "quem" || etapa === "forma") ? (
        <div className={`px-4 pt-3 pb-6 border-t ${darkMode ? "border-zinc-800" : "border-zinc-200"}`}>
          <button type="button" onClick={avancar} disabled={!podeAvancar || gerando || conta.divisao_ativa}
            data-e2e="pagar-avancar"
            className="w-full h-14 rounded-full bg-primary text-primary-foreground text-base font-black active:scale-[0.98] transition disabled:opacity-50">
            {gerando ? "Gerando..." : etapa === "quem"
              ? `CONTINUAR · R$ ${Number(valor).toFixed(2).replace(".", ",")}`
              : forma === "pix" && conta.pix_online ? "GERAR PIX" : "CONTINUAR"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
